/*
(C) 2020, 2021, 2022, 2023, 2024 EDISOFT


System : ESA Datalabs

File Name : sepp_ui/src/global/utils.ts

Author : EDISOFT, ACRI-ST, CGI

Creation Date : 2020-04-21

This file is subject to the terms and conditions defined in 'LICENSE.txt',
which is part of this source code package.
No part of the package, including this file, may be copied, modified, propagated,
or distributed except according to the terms contained in 'LICENSE.txt'.
*/

import { navigateTo } from "@datalabs/edl_common_ui";

export var goToRoute = (ev: Event, dest=null, extraInfo?: string) => {
  if (dest) {
    navigateTo(dest, extraInfo);
    return
  }
  ev.preventDefault();
  var e = ev.target as HTMLElement;
  // find the ancestral anchor which has the path
  while (e.parentNode && e.tagName != "A") {
    e = e.parentNode as HTMLElement;
  }
  if (e.tagName == "A") {
    var a = e as HTMLAnchorElement;
    // don't push the path if we're already there
    if (location.pathname != a.pathname) {
      navigateTo(a.pathname, extraInfo);
    }
  }
};

export type SplitUrl = {
  all: string,
  scheme: string,
  username: string,
  password: string,
  host: string,
  port: string,
  path: string,
}

export var constructUrl = (s: SplitUrl): string => {
  var auth = s.password ? `${s.username}:${s.password}` : s.username;
  return `${s.scheme}://${auth ? auth + "@" : ""}${s.host}${s.port ? ":" + s.port : ""}${s.path || ""}`;
}

const urlRegExp = new RegExp(
  "^(\\w+)://" +        // scheme
  "(?:" +               // optional authentication
  "([^:@]+)" +        // username
  "(?:\\:([^:@]+))" + // optional password
  "@)?" +
  "([^:/]+)" +          // host
  "(?:\\:([^/]+))?" +   // optional port
  "(/.*)?$"             // optional path + query + fragment
);
const groupNames = "all scheme username password host port path".split(" ");

export var deconstructUrl = (u: string): SplitUrl => {
  var matches = urlRegExp.exec(u);
  try {
    var retv = matches.reduce((r, m, i) => {
      r[groupNames[i]] = m;
      return r;
    }, {});
    return retv as SplitUrl;
  } catch (error) {
    return {} as SplitUrl;
  }
};

/**
 * Escapes HTML tags, including both opening and closing tags, within the input HTML content.
 *
 * This function replaces angle brackets (< and >) with their HTML entity equivalents (&lt; and &gt;).
 * It ensures that both opening and closing tags are properly escaped.
 *
 * @param {string} html - The input HTML content to process.
 * @returns {string} The processed HTML content with all tags escaped as HTML entities.
 */
export const escapeHtmlTags = (html: string) => {
  const tagRegex = new RegExp(`<(\\/)?([\\w-]+)([^>]*)>`, 'gi');
  return html.replace(tagRegex, (_, isClosing, tagName, attributes) => {
    const tagType = isClosing ? '/' : '';
    return `&lt;${tagType}${tagName}${attributes}&gt;`;
  });
};

/**
 * Calculates an exponential backoff delay in milliseconds.
 *
 * @param {number} initialDelay - The initial delay in milliseconds before the first attempt.
 * @param {number} baseDelay - The base delay in milliseconds that is scaled exponentially after first attempt.
 * @param {number} maxDelay - The maximum delay in milliseconds that can be reached.
 * @returns A function that takes an attempt number and computes the delay for that attempt,
 *          ensuring it does not exceed the maximum delay specified.
 */
export const exponentialBackoff = (
  initialDelay: number = 0,
  baseDelay: number = 100,
  maxDelay: number = 10000,
) => (attempt: number): number => {
  if (attempt === 0) {
    return initialDelay;
  }
  return Math.min(baseDelay * 2 ** (attempt - 1), maxDelay);
};
